<template>
  <section class="block mx-auto mt-32 max-w-448">
    <SitterAlertBanner v-if="serverError" :error="serverError" />
    <PoppinsCard>
      <CareTypeFormHeader careType="companion care" />
      <form id="create-profile-companion-care" novalidate @submit.prevent="handleSubmit">
        <div v-for="(field, index) in formFields" :key="index">
          <component :is="field.component" ref="childComponents" v-bind="field.props" />
        </div>

        <PoppinsButton
          id="formSubmit"
          :loading="loading"
          class="w-full my-8"
          variant="primary"
          value="Save & Continue"
          size="medium"
          type="submit"
        />
      </form>
    </PoppinsCard>
  </section>
</template>

<script lang="ts">
import { UPSERT_PROFILE_SITTER } from '../../../../graphql/profile/mutations';
import SitterAlertBanner from '../account/components/SitterAlertBanner.vue';
import CareTypeFormHeader from './components/CareTypeFormHeader.vue';
import YearsExperienceField from './components/YearsExperienceField.vue';
import AgeGroupsCheckbox from './components/AgeGroupsCheckbox.vue';
import ExperienceCheckbox from './components/ExperienceCheckbox.vue';
import QualificationsCheckbox from './components/QualificationsCheckbox.vue';
import MaxDistanceSelect from './components/MaxDistanceSelect.vue';
import ComfortablePetsRadio from './components/ComfortablePetsRadio.vue';
import ServiceRates from './components/ServiceRates.vue';
import BioArea from './components/BioArea.vue';
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import { companionCareAgeGroupsOptions } from './components/__types__/AgeTypes';
import { companionCareExperienceOptions } from './components/__types__/ExperienceTypes';
import { personCareQualificationsOptions } from './components/__types__/QualificationsTypes';

export default {
  name: 'ProviderCompanionCareForm',
  components: {
    SitterAlertBanner,
    CareTypeFormHeader,
    YearsExperienceField,
    AgeGroupsCheckbox,
    ExperienceCheckbox,
    QualificationsCheckbox,
    MaxDistanceSelect,
    ComfortablePetsRadio,
    ServiceRates,
    BioArea,
    PoppinsCard: Card,
    PoppinsButton: Button,
  },
  data(): any {
    return {
      formFields: [
        { component: 'YearsExperienceField', props: { careType: 'companion care' } },
        {
          component: 'AgeGroupsCheckbox',
          props: {
            ageRanges: companionCareAgeGroupsOptions,
            generateScreenReaderLabel: (optionText) =>
              `I have been a companion care giver for individuals that are ${optionText}.`,
            legend: 'What age groups do you have experience with?',
          },
        },
        {
          component: 'ExperienceCheckbox',
          props: {
            experienceTypes: companionCareExperienceOptions,
            generateScreenReaderLabel: (optionText) => `I have paid experience with ${optionText}`,
            legend: 'What types of care services for you have experience providing?',
          },
        },
        {
          component: 'QualificationsCheckbox',
          props: {
            options: personCareQualificationsOptions,
            generateScreenReaderLabel: (optionText) => `I have ${optionText} certification`,
            legend: 'Do you have any certifications?',
          },
        },
        { component: 'MaxDistanceSelect' },
        { component: 'ComfortablePetsRadio' },
        { component: 'ServiceRates' },
        { component: 'BioArea' },
      ],
      serverError: null,
      loading: false,
    };
  },
  computed: {
    firstInvalidComponent() {
      return this.$refs.childComponents.find((childComponent) => {
        return childComponent.validate && !childComponent.validate();
      });
    },
  },
  methods: {
    handleSubmit() {
      this.$refs.childComponents.forEach((childComponent) => {
        if (childComponent.validate) {
          childComponent.validate();
        }
      });

      return this.firstInvalidComponent ? this.sendFocusToError() : this.upsertProfile();
    },
    sendFocusToError() {
      this.firstInvalidComponent.focusElement?.();
    },
    async upsertProfile() {
      try {
        await this.$apollo.mutate({
          mutation: UPSERT_PROFILE_SITTER,
          variables: {
            profile: {
              companionCare: {
                yearsExperience: Number(this.$store.getters.getFormField('yearsExperience')),
                ageGroups: this.$store.getters.getFormField('ageGroups'),
                experience: this.$store.getters.getFormField('experience'),
                qualifications: this.$store.getters.getFormField('qualifications'),
                distanceWillingToTravel: Number(this.$store.getters.getFormField('distanceWillingToTravel')),
                comfortableWithPets: this.$store.getters.getFormField('comfortableWithPets') === 'true',
                serviceRates: this.$store.getters.selectedServiceRates,
                bio: this.$store.getters.getFormField('bio'),
              },
            },
          },
        });
        console.log('profile created'); // eslint-disable-line no-console
        this.redirectToVerifyIdentity();
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        this.serverError = new Error('unknown-error');
      }
    },
    redirectToVerifyIdentity() {
      this.loading = true;
      window.location.href = '/sitter/verify_identity';
    },
  },
};
</script>
